/**
 * graceWindow — pure, firebase-freie Ableitung des befristeten Grace-Fenster-
 * Status je Take-away-Feature (Grace-Window, architecture-grace-window.md §5.2).
 *
 * Baut ausschließlich auf den pure `entitlements`-Bausteinen auf — insbesondere
 * dem GETEILTEN Grace-Reader `readGraceUntilMs` (EINE Parse-Implementierung im
 * FE, kein Copy-Paste, §6.1). Kein Firebase-/API-Import.
 *
 * Zwei Verantwortlichkeiten:
 *   - `getGraceStatus`: reine Status-/Restlaufzeit-Ableitung (für Banner-Anzeige).
 *   - `shouldShowGraceCountdown`: Sichtbarkeits-Entscheidung des Countdown-Banners,
 *     inkl. Flag-Kurzschluss (Null-Delta §9) und Premium-/Permanent-Unterdrückung.
 *
 * @module config/graceWindow
 */

import {
  resolveTier,
  hasMinTier,
  hasPermanentGrandfathering,
  readGraceUntilMs,
  TIER_V2_FLAG,
  type SellerLike,
  type GrandfatheredFeature,
} from './entitlements';

/** Millisekunden pro Kalendertag (UTC-neutral, für Restlaufzeit-Berechnung). */
const MS_PER_DAY = 86_400_000;

/**
 * Schwelle (Restlaufzeit in Tagen), ab der das Banner in Monaten statt Tagen
 * anzeigt: `remainingDays >= 60` ⇒ Monats-Anzeige `floor(remainingDays/30)`
 * (§5.2 / §8.3). Exportiert, damit Banner (M-G4) und Tests dieselbe Konstante
 * referenzieren.
 */
export const GRACE_MONTHS_DISPLAY_MIN_DAYS = 60;

/**
 * Schwelle (Restlaufzeit in Tagen), ab der das Banner die Warn-Variante zeigt:
 * `remainingDays <= 14` ⇒ dringlich (§5.2 / §8.5). Exportiert für M-G4 + Tests.
 */
export const GRACE_URGENT_THRESHOLD_DAYS = 14;

/**
 * Diskriminierte Union des Grace-Status eines Features. `'none'` deckt ALLE
 * Nicht-aktiv-Fälle ab: fehlend, kaputt, abgelaufen, exakt-jetzt. Im `'active'`-
 * Fall gilt stets `remainingDays >= 1` (strikt zukünftiges Ablaufende).
 */
export type GraceStatus =
  | { state: 'none' }
  | { state: 'active'; expiresAtMs: number; remainingDays: number };

/**
 * Leitet den Grace-Status eines Features aus dem rohen Seller-Doc ab (§5.2).
 *
 * Fail-closed: fehlende/kaputte Grace-Daten (`readGraceUntilMs` ⇒ `null`) sowie
 * ein Ablaufzeitpunkt `<= nowMs` (strikt `>`; exakt-jetzt zählt als abgelaufen)
 * ⇒ `{ state: 'none' }`. Sonst `{ state: 'active', expiresAtMs, remainingDays }`
 * mit `remainingDays = Math.ceil((expiresAtMs - nowMs) / 86_400_000)` (⇒ ≥ 1).
 *
 * @param sellerData - Roh-Seller-Doc-Daten (oder `null`).
 * @param feature - Der zu prüfende Feature-Key.
 * @param nowMs - Vergleichs-Jetzt in ms (Default `Date.now()`; Injektion für Tests).
 * @returns `GraceStatus` — `'active'` nur bei strikt zukünftigem, parsebarem Ablauf.
 */
export function getGraceStatus(
  sellerData: SellerLike | null | undefined,
  feature: GrandfatheredFeature,
  nowMs: number = Date.now()
): GraceStatus {
  const expiresAtMs = readGraceUntilMs(sellerData, feature);
  if (expiresAtMs === null || expiresAtMs <= nowMs) {
    return { state: 'none' };
  }
  const remainingDays = Math.ceil((expiresAtMs - nowMs) / MS_PER_DAY);
  return { state: 'active', expiresAtMs, remainingDays };
}

/**
 * Entscheidet (pure), ob das Grace-Countdown-Banner für ein Feature sichtbar ist
 * (§5.2). Reihenfolge (fail-closed, jeder Kurzschluss ⇒ kein Banner):
 *
 *   1. `!TIER_V2_FLAG` ⇒ `false` (Null-Delta §9 — Banner existiert nur bei aktivem Flag).
 *   2. Tier ≥ premium ⇒ `false` (Upgrade während des Fensters ⇒ kein Countdown mehr).
 *   3. permanent grandfathered ⇒ `false` (Dauer-Bestandsschutz ⇒ kein befristeter Countdown).
 *   4. sonst: `getGraceStatus(...).state === 'active'`.
 *
 * @param sellerData - Roh-Seller-Doc-Daten (oder `null`).
 * @param feature - Der zu prüfende Feature-Key.
 * @param nowMs - Vergleichs-Jetzt in ms (Default `Date.now()`; Injektion für Tests).
 * @returns `true`, nur wenn Flag an, kein Premium, kein Permanent und Grace aktiv.
 */
export function shouldShowGraceCountdown(
  sellerData: SellerLike | null | undefined,
  feature: GrandfatheredFeature,
  nowMs: number = Date.now()
): boolean {
  if (!TIER_V2_FLAG) {
    return false;
  }
  if (hasMinTier(resolveTier(sellerData), 'premium')) {
    return false;
  }
  if (hasPermanentGrandfathering(sellerData, feature)) {
    return false;
  }
  return getGraceStatus(sellerData, feature, nowMs).state === 'active';
}
